import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { AITranslationModel } from '../model/ai-translation.model';
import { OriginalInputService } from './original-input.service';
import { SuggestionService } from './suggestion.service';
import type { AITranslationState, ContentItem } from '../type/ai-translation.type';
import type { HistoryDetail } from '@/services/history/type/history.type'
import { HistoryService } from '@/services/history/service/history.service';
import { Step, TaskType } from '../type/ai-translation.enum';
import { CheckSensitiveWordDTO, GetTechTopicDTO, AddTranslationAsyncTaskDTO, ExportTranslationDTO } from './ai-translation.repository.interface';
import formatNumber from 'library-@/utils/formatNumber';
import { I18nService } from '@analytics/shared/services/i18n.interface';
import { Message } from '@patsnap/tahiti-core/common/message';
import type VueI18n from 'vue-i18n';
import { VueRouterService } from '@analytics/shared/services/vue-router-service.interface'
import { HISTORY_DEFAULT_PAGE, HISTORY_LIST_LIMIT, TITLE_SLICE_LENGTH } from '@/constants';
import moment from 'moment';

export const AITranslationService = Symbol('AITranslationService');

export interface AITranslationService {
    getModelData: () => AITranslationState;
    readonly onStatesChange: Event<AITranslationState>;
    initialize: (taskId?: string) => Promise<void>;
    updateTitle: (title: string) => void;
    updateResult: (result: ContentItem[]) => void;
    updateDrawerStatus: (status: boolean) => void;
    updateUpdatedAt: () => void;
    exportTranslation: () => Promise<void>;
    checkSensitiveWord: (text: string) => Promise<string>;
    handleSubmitOriginalInput: () => Promise<void>;
    handlePrepareTranslate: () => Promise<void>;
    handleBackToEdit: () => Promise<void>;
    handleGoSuggestions: () => Promise<void>;
    handleGenerateTranslation: () => Promise<void>;
    handleGoResult: () => Promise<void>;
    handlePreviewTranslation: () => Promise<void>;
    handleNewTask: () => Promise<void>;
}

const ASYNC_THRESHOLD = 1000;

@injectable()
export class AITranslationServiceImpl implements AITranslationService {
    private _onStatesChangeEmitter = new Emitter<AITranslationState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(VueRouterService)
        private _router: VueRouterService,
        @inject(Message)
        private _message: Message,
        @inject(I18nService)
        private _i18n: VueI18n,
        @inject(AITranslationModel)
        private _model: AITranslationModel,
        @inject(OriginalInputService)
        private _originalInputService: OriginalInputService,
        @inject(SuggestionService)
        private _suggestionService: SuggestionService,
        @inject(HistoryService)
        private _historyService: HistoryService,
        @inject(CheckSensitiveWordDTO)
        private _checkSensitiveWordDTO: CheckSensitiveWordDTO,
        @inject(GetTechTopicDTO)
        private _getTechTopicDTO: GetTechTopicDTO,
        @inject(AddTranslationAsyncTaskDTO)
        private _addTranslationAsyncTaskDTO: AddTranslationAsyncTaskDTO,
        @inject(ExportTranslationDTO)
        private _exportTranslationDTO: ExportTranslationDTO,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData() {
        return this._model.modelData;
    }

    private _setModelData(value: Partial<AITranslationState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    private _stepTo(step: Step) {
        this._setModelData({ step });
    }

    public async updateTitle(title: string) {
        this.putHistory({ title });
        this._setModelData({ title });
    }

    private async _createTaskId(): Promise<string> {
        const taskId = await this._historyService.createTaskId('AI_TRANSLATION');
        return taskId;
    }

    private _reset() {
        this._setModelData({
            taskId: '',
            step: Step.ORIGINAL_INPUT,
            taskType: undefined,
            showDrawer: false,
            title: '',
            result: [],
        });
    }

    private _resetAll() {
        this._reset();
        this._originalInputService.reset();
        this._suggestionService.reset();
    }

    public async initialize(taskId?: string): Promise<void> {
        this._setModelData({ isInitializing: true });
        this._resetAll();

        if (taskId) {
            this._setModelData({ taskId });
            await this.getHistoryDetail(taskId);
        } else {
            this._setModelData({ taskId });

        }

        this._setModelData({ isInitializing: false });
    }

    public async handleSubmitOriginalInput() {
        const modelData = this.getModelData();
        const taskId = modelData.taskId || await this._createTaskId();
        this._setModelData({ taskId });

        let title = modelData.title;

        if (!title) {
            const originalInput = this._originalInputService.getModelData().input;
            title = originalInput.slice(0, TITLE_SLICE_LENGTH);
            this.updateTitle(title);
        }

        this._historyService.getHistoryList({ page: HISTORY_DEFAULT_PAGE, limit: HISTORY_LIST_LIMIT });

        this._suggestionService.initialize(async () => {
            const originalInputModel = this._originalInputService.getModelData();
            const techTopic = await this._getTechTopicDTO.fetch({ ...originalInputModel, taskId, currentStep: Step.SUGGESTIONS });
            return techTopic;
        });
        this._stepTo(Step.SUGGESTIONS);
    }

    public async handlePrepareTranslate() {
        const originalInput = this._originalInputService.getModelData().input;
        const taskType = originalInput.length > ASYNC_THRESHOLD ? TaskType.ASYNC : TaskType.SYNC;

        if (taskType === TaskType.ASYNC) {
            const params = {
                taskId: this.getModelData().taskId,
                currentStep: Step.RESULT, // 需要结合任务类型、状态进行还原
            }

            this._addTranslationAsyncTaskDTO.fetch(params);
        }

        this._setModelData({ taskType });
        this._stepTo(Step.GENERATE);
    }

    public async handleBackToEdit() {
        this._stepTo(Step.ORIGINAL_INPUT);
    }

    public async handleGoSuggestions() {
        this._stepTo(Step.SUGGESTIONS);
    }

    public async handleGenerateTranslation() {
        this._stepTo(Step.GENERATE);
    }

    public async handleGoResult() {
        this.putHistory({ detail_key_values: { '$.current_step': Step.RESULT } });
        this._stepTo(Step.RESULT);
    }

    public async handlePreviewTranslation() {
        this.putHistory({ detail_key_values: { '$.current_step': Step.PREVIEW } });
        this._stepTo(Step.PREVIEW);
    }

    public async handleNewTask() {
        await this.initialize();
    }


    public updateDrawerStatus(showDrawer: boolean) {
        this._setModelData({ showDrawer });
    }

    public updateUpdatedAt() {
        const now = moment().format('YYYY-MM-DD HH:mm');
        this._setModelData({ updatedAt: now });
    }

    public updateResult(result: ContentItem[]) {
        this._setModelData({ result });
    }

    public getGraphResult() {
        const result = this.getModelData().result;
        return result.reduce((acc, cur) => {
            if (cur.graphIndex < 0) return acc;
            const currentGraph = acc.find(
                item => item.graphIndex === cur.graphIndex
            );

            if (currentGraph) {
                currentGraph.original = `${currentGraph.original}${cur.original}`;
                currentGraph.translation = `${currentGraph.translation}${cur.translation}`;
            } else {
                acc.push(cur);
            }

            return acc;
        }, []);
    }


    public getWordsCount(result: { original: string, translation: string }[]) {
        const { origin, translated } = result.reduce(
            (acc, cur) => {
                acc.origin += cur.original?.length ?? 0;
                acc.translated += cur.translation?.length ?? 0;
                return acc;
            },
            { origin: 0, translated: 0 }
        );

        return {
            origin: formatNumber(origin),
            translated: formatNumber(translated),
        };
    }


    private _getExportData() {
        const title = this.getModelData().title || this._i18n.t('aiTranslation.untitled');
        let original = '';
        let translation = '';
        const graphResult = this.getGraphResult();
        graphResult.forEach((item) => {
            original += `${item.original}\n`;
            translation += `${item.translation}\n`;
        });

        return { title, original, translation };
    }

    public async exportTranslation() {
        const exportData = this._getExportData();
        const wordUrl = await this._exportTranslationDTO.fetch(exportData);
        window.open(wordUrl, '_self');
    }

    public putHistory<T>(params: T) {
        const taskId = this.getModelData().taskId;
        this._historyService.putHistory({ task_id: taskId, ...params });
    }

    // 初始化各步骤数据
    private _initStepsData(historyDetail: HistoryDetail) {
        const {
            asyncStatus,
            taskId,
            title,
            updatedAt,
            taskType,
            detail = {},
        } = historyDetail;

        const {
            currentStep,
            content = [],
            keywords = [],
            sourceLang,
            input,
            targetLang,
            techTopic,
        } = detail;

        let step = currentStep;
        if (step === Step.RESULT && taskType === TaskType.ASYNC && asyncStatus !== 'Complete') {
            step = Step.GENERATE;
        }

        if (asyncStatus === 'Failed') {
            step = Step.ORIGINAL_INPUT;

            this._message.closeAll();
            this._message.error({
                message: this._i18n.t('aiTranslation.taskFailedTip') as string,
                showClose: true,
            });
        }

        this._setModelData({
            isInitializing: false,
            step,
            taskId,
            taskType,
            title,
            result: content,
            updatedAt,
            showDrawer: step === Step.RESULT,
        })

        this._originalInputService.initialize({
            input,
            sourceLang,
            targetLang,
            isSupportLang: true,
        });

        this._suggestionService.initState({
            isLoading: false,
            techTopic,
            suggestions: keywords,
            activeSuggestionIdx: 0,
        });
    }

    // 获取历史记录详情
    public async getHistoryDetail(id: string) {
        try {
            const historyDetail = await this._historyService.getHistoryDetail(id);
            this._initStepsData(historyDetail);
        } catch (err) {
            this._message.closeAll();
            this._message.error({
                message: this._i18n.t('aiTranslation.noTaskPermission') as string,
                showClose: true,
            });

            this._resetAll();
            this._router.push('/translation');
        }
    }

    public async checkSensitiveWord(text: string): Promise<string> {
        if (!text) return Promise.resolve('');
        const res = await this._checkSensitiveWordDTO.fetch(text);
        if (res) {
            this._message.closeAll();
            this._message.error({
                message: this._i18n.t('aiTranslation.sensitiveTip') as string,
                showClose: true,
            });
        }
        return res;
    }
}